import { reaction } from 'mobx'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useContext, useEffect } from 'react'
import { Navigate, Outlet, useLocation, useMatches, useNavigate } from 'react-router'

import { useEntityAccess } from '@/hooks/useEntityAccess'
import { ErrorPage } from '@/pages/Error'
import { UserStoreContext } from '@/stores/UserStore'
import { userHasAccess } from '@/utils/routes'

import FeedbackButton from '../FeedbackButton/FeedbackButton'

/**
 * Routes constants
 * Configures the routes for the accounts page and userStore logic
 *
 * Note: we can expand this to include role based defaults in the future, e.g.
 * ROUTES.FALLBACK['Student] or ROUTES.FALLBACK['Instructor'] to redirect to the
 * appropriate entity-scoped route for different roles.
 */
export const ROUTES = {
  LOGIN: '/login',
  ACCOUNTS: '/accounts',
  ADMIN: '/admin',
  FALLBACK: '/dashboard',
  COMPLETE_PROFILE: '/complete-profile',
  ADD_RECOVERY_METHOD: '/add-recovery-method',
}

/**
 * The prompts a user is held at before reaching the app, in the order they are
 * asked. A check for one of them must not redirect away from another, or the
 * two bounce the user between themselves.
 */
const ONBOARDING_ROUTES = [ROUTES.COMPLETE_PROFILE, ROUTES.ADD_RECOVERY_METHOD]

/**
 * Forbidden ErrorPage component
 *
 * @param {*} props
 * @param {*} isXverseAdmin - Whether the user is an admin
 * @param {*} activeEntity - The active entity
 * @returns {JSX.Element} ErrorPage component
 */
const Forbidden = ({ isXverseAdmin = false, activeEntity = null }) => (
  <ErrorPage
    heading="Access denied"
    message="You do not have access to this page."
    buttonText="Go Back"
    buttonLink={isXverseAdmin && !activeEntity ? '/admin' : '/accounts'}
  />
)

Forbidden.propTypes = {
  isXverseAdmin: PropTypes.bool,
  activeEntity: PropTypes.object,
}

/**
 * Resolves the accounts redirect path based on the user's active entity
 * @param {*} userStore
 * @returns {string|null}
 */
const resolveAccountsRedirect = (userStore) => {
  const { isXverseAdmin, activeEntity, activeOrganization } = userStore

  // A facility context wins; otherwise an organization context; otherwise admins
  // land on /admin and everyone else has no landing target.
  if (activeEntity) return `/accounts/${activeEntity}${ROUTES.FALLBACK}`
  if (activeOrganization) return `/organizations/${activeOrganization}/dashboard`
  return isXverseAdmin ? ROUTES.ADMIN : null
}

/**
 * PrivateRoute
 *
 * If a user is unauthenticated and attempts to access this route, they will be
 * redirected to the login page, otherwise we return the requested route.
 */
const PrivateRoute = observer(() => {
  const userStore = useContext(UserStoreContext)
  const location = useLocation()
  const navigate = useNavigate()
  const matches = useMatches()
  // Live access flags for the active entity, used to gate account feature
  // switches. Called unconditionally to keep hook order stable across renders.
  const { entityAccess, isEntityAccessResolved } = useEntityAccess(userStore.activeEntity)

  /**
   * If the user's active entity changes, navigate to the accounts page which
   * will redirect to the corresponding entity's landing page for the user,
   * including cases where the user is an xverse admin.
   *
   * uses `reaction` to listen for changes to the active entity
   * @link https://mobx.js.org/reactions.html#reaction
   *
   * Hooks must run before any conditional returns below — otherwise the hook
   * order changes between renders (e.g. when redirecting to /complete-profile
   * for invited users) and React throws "Rendered more hooks than during the
   * previous render."
   */
  useEffect(() => {
    const dispose = reaction(
      // Composite key so switching between facilities AND organizations both
      // trigger a redirect (e.g. org-to-org keeps activeEntity null).
      () => `${userStore.activeEntity ?? ''}|${userStore.activeOrganization ?? ''}`,
      () => {
        const target = resolveAccountsRedirect(userStore)
        if (target) navigate(target)
      },
    )

    return () => dispose()
  }, [userStore, navigate])

  /**
   * Short-circuit #1 if user is not authenticated
   * Redirect to login with current location passed along
   */
  if (!userStore.isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: ROUTES.LOGIN,
          state: { from: location },
        }}
      />
    )
  }
  // User is authenticated, set Sentry user info.
  // Could this be moved to only be called on page load, not on every route change?
  userStore.setSentryUser()

  /**
   * Short-circuit #1.5 if user needs to complete their profile (missing name)
   * Redirect to complete-profile unless already there
   */
  if (userStore.needsProfileCompletion && location.pathname !== ROUTES.COMPLETE_PROFILE) {
    return <Navigate to={ROUTES.COMPLETE_PROFILE} replace />
  }

  /**
   * Short-circuit #1.6 if the account has no way back in other than its primary
   * address (RSCX-377). That address is usually employer-owned, so an account
   * resting on it alone is lost with the job — every user adds a backup email
   * or phone number before going any further. Ordered after profile completion
   * so a brand-new user finishes one prompt at a time.
   */
  /**
   * The exemption covers *both* prompts, not only this one. A user who owes a
   * name and a recovery method sits on /complete-profile, which this check
   * would otherwise push to /add-recovery-method while the check above pushed
   * it straight back — a redirect loop that renders nothing at all, on every
   * route, because this component wraps them (RSCX-13).
   */
  if (userStore.needsRecoveryMethod && !ONBOARDING_ROUTES.includes(location.pathname)) {
    return <Navigate to={ROUTES.ADD_RECOVERY_METHOD} replace />
  }

  /**
   * Short-circuit #2 for admin routes access
   * If the user isn't isXverseAdmin, they can't access the admin routes
   */
  const isAdminSection =
    location.pathname === ROUTES.ADMIN || location.pathname.startsWith(`${ROUTES.ADMIN}/`)
  if (isAdminSection && !userStore.isXverseAdmin) {
    return <Forbidden />
  }

  /**
   * Short-circuit #2.5 for organization routes (RSCX-320).
   *
   * Org routes (/organizations/:organizationId/...) are gated on org membership
   * rather than entity roles, so they bypass the entity-scoped userHasAccess
   * check below. An Org Owner/Admin of the URL's org — or any Xverse Admin —
   * may enter; everyone else is forbidden.
   */
  if (location.pathname.startsWith('/organizations/')) {
    const organizationId = matches.map((m) => m.params?.organizationId).find(Boolean)
    const isOrgMember = (userStore.organizationRoles ?? []).some(
      (r) => r.organizationId === organizationId,
    )
    return userStore.isXverseAdmin || isOrgMember ? (
      <>
        <Outlet />
        <FeedbackButton className="fixed right-4 bottom-18 z-50 md:bottom-4" />
      </>
    ) : (
      <Forbidden isXverseAdmin={userStore.isXverseAdmin} activeEntity={userStore.activeEntity} />
    )
  }

  // Redirect based on access rules if user is on /accounts or /accounts/
  if (location.pathname === ROUTES.ACCOUNTS || location.pathname === `${ROUTES.ACCOUNTS}/`) {
    const redirectPath = resolveAccountsRedirect(userStore)
    if (redirectPath) return <Navigate to={redirectPath} />
    // No redirect target means the user has no entity access and isn't an Xverse
    // admin (e.g. an Xverse Instructor Candidate who hasn't been assigned to an
    // account yet). Render Forbidden so they see a real page instead of a white
    // screen.
    return (
      <Forbidden isXverseAdmin={userStore.isXverseAdmin} activeEntity={userStore.activeEntity} />
    )
  }

  /**
   * Checks if the user has access to the current page given the route match's
   * required roles, current active entity, and user's entity roles
   */
  const leaf = matches.at(-1)
  // An account-access switch declared on any matched route (typically the
  // section's parent, e.g. the Custom Courses hub) gates every route beneath
  // it, so direct deep links are blocked too — not just the nav item.
  const requiredAccess = matches.map((m) => m?.handle?.access).find(Boolean) ?? null

  // Wait for the entity's access flags before deciding on a gated route, so we
  // don't flash Access denied on a direct deep link while they load. Keyed on
  // "resolved" rather than "loading" because the cache can hold the previous
  // entity's flags mid-switch: React Query reports not-loading, but the flags
  // in hand don't describe this account yet (RSCX-385).
  if (requiredAccess && userStore.activeEntity && !isEntityAccessResolved) {
    return null
  }

  const canAccess =
    leaf &&
    userHasAccess({
      isXverseAdmin: userStore.isXverseAdmin,
      isXverseInstructorCandidate: userStore.isXverseInstructorCandidate,
      requiredRoles: leaf?.handle?.roles ?? [],
      requiredAccess,
      entityAccess,
      entityRoles: userStore.user?.entityRoles,
      activeEntityId: userStore.activeEntity,
    })

  return canAccess ? (
    <>
      <Outlet />
      <FeedbackButton className="fixed right-4 bottom-18 z-50 md:bottom-4" />
    </>
  ) : (
    <Forbidden isXverseAdmin={userStore.isXverseAdmin} activeEntity={userStore.activeEntity} />
  )
})

export default PrivateRoute
